import { parsePrice } from "helpers/price";
import { HorizontalLine } from "pages/Dashboard/components/styled";
import { useMemo } from "react";
import styled from "styled-components";

const StyledCard = styled.div`
  border: 1px solid #eaecf0;
  border-radius: 12px;
  img {
    border-radius: 100%;
  }
`;

const StyledHeader = styled.div`
  background-image: url("/ai_background.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 10px;
  border-radius: 12px 12px 0 0;
  border-bottom: 1px solid #f2f4f7;
`;

export function AIRecommendations({
  topResultsAmount,
  highestCommercialPayers,
  lowestCommercialPayers,
  highestVsLowestCommercial,
  contractInsightsData,
  isLoadingContractInsights,
}: {
  topResultsAmount: number;
  highestCommercialPayers: string[];
  lowestCommercialPayers: string[];
  highestVsLowestCommercial?: number | null;
  contractInsightsData?: any;
  isLoadingContractInsights?: boolean;
}) {
  const showData = useMemo(() => {
    return highestCommercialPayers?.length && lowestCommercialPayers?.length;
  }, [highestCommercialPayers, lowestCommercialPayers]);

  const hasContractInsights = useMemo(() => {
    return (
      contractInsightsData?.recommendations &&
      (contractInsightsData.recommendations.lowPayingPayerOpportunities
        ?.length > 0 ||
        contractInsightsData.recommendations.implantReimbursementOpportunities
          ?.length > 0 ||
        contractInsightsData.recommendations.mpdrComparisonOpportunities
          ?.length > 0 ||
        contractInsightsData.recommendations.belowMedicareOpportunities
          ?.length > 0)
    );
  }, [contractInsightsData]);

  const renderRecommendationSection = (
    title: string,
    recommendations: any[],
    type: string
  ) => {
    if (!recommendations?.length) return null;

    return (
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-new-900 mb-2">
          {title}
        </h4>
        <ul className="list-disc pl-3">
          {recommendations.map((rec, index) => (
            <li key={index} className="mb-2">
              <div className="text-sm text-gray-700">
                {type === "lowPaying" && <div>{rec.recommendation}</div>}
                {type === "implantReimbursement" && (
                  <div>{rec.recommendation}</div>
                )}
                {type === "mpdrComparison" && <div>{rec.recommendation}</div>}
                {type === "belowMedicare" && <div>{rec.recommendation}</div>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };
  return (
    <div className="p-4 w-full lg:w-6">
      <StyledCard>
        <StyledHeader className="flex gap-4 align-items-center">
          <img src="/ai.png" alt="ai logo" />
          <div className="flex flex-column gap-1">
            <p className="text-xl font-semibold text-gray-new-900">
              AI Recommendations
            </p>
            <p className="text-sm text-gray-new-600 font-normal">
              Strengthen your future contract negotiations with powerful AI
              insights.{" "}
            </p>
          </div>
        </StyledHeader>
        <div className="p-4">
          {(() => {
            if (isLoadingContractInsights) {
              return (
                <div className="w-full h-full flex justify-content-center items-center py-4">
                  <p className="text-gray-new-600 text-sm font-normal">
                    Loading AI insights...
                  </p>
                </div>
              );
            }

            if (hasContractInsights) {
              return (
                <>
                  <p className="text-md font-semibold text-gray-new-900 mb-3">
                    Cost Saving Opportunities
                  </p>

                  {renderRecommendationSection(
                    "Renegotiate with Low-Paying Payers",
                    contractInsightsData?.recommendations
                      ?.lowPayingPayerOpportunities,
                    "lowPaying"
                  )}

                  {renderRecommendationSection(
                    "Implant Reimbursement Alignment",
                    contractInsightsData?.recommendations
                      ?.implantReimbursementOpportunities,
                    "implantReimbursement"
                  )}

                  {renderRecommendationSection(
                    "Multiple Procedure Payment Policy (MPPR)",
                    contractInsightsData?.recommendations
                      ?.mpdrComparisonOpportunities,
                    "mpdrComparison"
                  )}

                  {renderRecommendationSection(
                    "Target Below-Medicare Codes",
                    contractInsightsData?.recommendations
                      ?.belowMedicareOpportunities,
                    "belowMedicare"
                  )}

                  {/* Fallback to original recommendations if no contract insights */}
                  {(() => {
                    const hasNoRecommendations =
                      !contractInsightsData?.recommendations ||
                      Object.values(contractInsightsData.recommendations).every(
                        (arr: unknown) => Array.isArray(arr) && arr.length === 0
                      );

                    if (hasNoRecommendations && showData) {
                      return (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-new-900 mb-2">
                            Rate Comparison Opportunities
                          </h4>
                          <ul className="list-disc pl-3">
                            <li className="mb-2">
                              <div className="text-sm text-gray-700">
                                <span className="font-semibold">
                                  Renegotiate with Low-Paying Payers:
                                </span>
                                <br />
                                Focus on{" "}
                                {lowestCommercialPayers?.join(", ") || "N/A"},
                                the lowest-paying payer
                                {lowestCommercialPayers?.length > 1 && "s"}, to
                                address the {highestVsLowestCommercial}%
                                variance in your top {topResultsAmount} CPT
                                codes.
                              </div>
                            </li>
                          </ul>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
              );
            }

            return (
              <div className="w-full h-full flex justify-content-center items-center">
                <p className="text-gray-new-600 text-sm font-normal">
                  {showData
                    ? "No contract insights available"
                    : "No data to show"}
                </p>
              </div>
            );
          })()}
        </div>
        <HorizontalLine className="border-gray-new-200 my-0" />
        <div className="p-4" />
      </StyledCard>
    </div>
  );
}
